import Image from 'next/image'

/**
 * BrandFigure — an in-article visual from the July 2026 social media kit
 * (social-media-kit/gen.js → public/social/*.webp).
 *
 * Why these exist in articles at all: the stat cards carry the article's
 * single most citable claim in a shareable, branded frame. Embedded next
 * to the paragraph that makes the claim, they (a) break up long text
 * walls, (b) give Pinterest/social shares a designed image instead of a
 * random screenshot, and (c) keep the claim attached to its source line,
 * which is baked into the image.
 *
 * Rules of use:
 *  - One per article, placed where the claim it states is discussed.
 *  - The alt text must state the actual claim (screen readers get the
 *    same fact sighted readers get), not "branded social card".
 *  - Sized down (max-w-md) so it reads as an editorial pull-figure, not
 *    a full-bleed hero. Tall pin formats get max-w-sm via `variant="pin"`.
 */
interface BrandFigureProps {
  src: string
  alt: string
  width: number
  height: number
  /** `pin` narrows tall 2:3 images so they don't dominate the column. */
  variant?: 'square' | 'pin'
  caption?: string
}

export default function BrandFigure({
  src,
  alt,
  width,
  height,
  variant = 'square',
  caption,
}: BrandFigureProps) {
  return (
    <figure
      className={`not-prose my-8 mx-auto ${variant === 'pin' ? 'max-w-sm' : 'max-w-md'}`}
    >
      <Image
        src={src}
        alt={alt}
        width={width}
        height={height}
        className="w-full h-auto rounded-2xl shadow-md ring-1 ring-black/5"
        sizes="(max-width: 640px) 90vw, 448px"
      />
      {caption && (
        <figcaption className="mt-3 text-center text-sm text-gray-500">
          {caption}
        </figcaption>
      )}
    </figure>
  )
}
